import { useLazyFetchLocationsQuery } from '@api/Trinium/Endpoints/Location/slice';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import {
	InputAdornment,
	LinearProgress,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	TextField,
	Theme,
	useMediaQuery
} from '@mui/material';
import SearchIcon from '@mui/icons-material/SearchTwoTone';
import { AddBoxTwoTone, EditTwoTone } from '@mui/icons-material';
import { PopupContentProps } from '@forms/Components/Dropdowns/ButtonDropdown';
import { useCallback, useEffect, useRef, useState } from 'react';
import useDebouncer from '@hooks/useDebounce';
import { useAppDispatch } from '@reduxStore';
import { queueModal } from '@components/Modals/modalSlice';
import { mapLocListItemToLegLoc } from '@api/Trinium/Endpoints/Location/types';
import { LegLocation } from '@api/Trinium/Endpoints/Leg/types';

import styles from './Form.module.css';

export default function LocationListContent({
	onChange,
	closeMenu,
	legLocation,
	selectionOnly,
	onEditLocation,
	...props
}: PopupContentProps & {
	legLocation?: LegLocation;
	selectionOnly: boolean;
	fixedCacheKey?: string;
	onEditLocation?: (locationId: string) => void;
}) {
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	const [searchInput, setSearchInput] = useState('');
	const { number: terminalNumber } = useCurrentTerminal();
	const [fetchLocations, { data: locations, isFetching }] = useLazyFetchLocationsQuery();
	const appDispatch = useAppDispatch();
	const searchInputRef = useRef<HTMLInputElement>(null);

	const editLocation = useCallback(
		(locationId: string) => {
			if (onEditLocation) {
				onEditLocation(locationId);
			} else {
				appDispatch(
					queueModal({
						Component: 'consigneeModal',
						Props: { id: locationId }
					})
				);
			}
		},
		[onEditLocation, appDispatch]
	);

	const fetchOnSearch = useCallback(() => {
		if (searchInput.length > 2) {
			void fetchLocations({
				terminalNumber: terminalNumber,
				startsWith: searchInput
			});
		}
	}, [searchInput, fetchLocations, terminalNumber]);

	/** On Render, focus the search input except in mobile */
	useEffect(() => {
		if (!isMobile) {
			searchInputRef.current?.focus();
		}
	}, [isMobile]);

	useDebouncer(fetchOnSearch, [searchInput], 1000);

	return (
		<div className={styles['locations-container']}>
			<TextField
				fullWidth
				placeholder='Start Typing to Search...'
				value={searchInput}
				onChange={(event) => {
					setSearchInput(event.target.value);
				}}
				variant='standard'
				slotProps={{
					input: {
						startAdornment: (
							<InputAdornment
								position='start'
								className={styles['search-ico']}
							>
								<SearchIcon fontSize='small' />
							</InputAdornment>
						),
						endAdornment: !selectionOnly ? (
							<InputAdornment
								position='start'
								className={styles['add-location-ico']}
							>
								<AddBoxTwoTone
									titleAccess='Add a New Location'
									fontSize='small'
									onClick={() => {
										appDispatch(
											queueModal({
												Component: 'consigneeModal',
												Props: {
													apiFixedCacheKey: props.fixedCacheKey
												}
											})
										);
										closeMenu();
									}}
								/>
							</InputAdornment>
						) : null
					}
				}}
				inputRef={searchInputRef}
			/>
			<LinearProgress className={!isFetching ? styles.hidden : undefined} />
			<List
				disablePadding
				dense
				className={styles['location-list']}
			>
				{!locations && legLocation && (
					<ListItemButton
						onClick={() => {
							onChange(legLocation);
						}}
						className={styles['list-item']}
					>
						<ListItemText
							primary={`${legLocation.name ?? ''} (${legLocation.id})`}
							secondary={`${legLocation.addr1 ?? ''} ${legLocation.city ?? ''}, ${legLocation.state ?? ''} ${legLocation.zip ?? ''}`}
						/>
						{!selectionOnly && (
							<ListItemIcon
								title='Edit Location'
								className={styles['list-item-ico']}
								onClick={(event) => {
									event.stopPropagation();
									event.preventDefault();
									closeMenu();
									editLocation(legLocation.id);
								}}
							>
								<EditTwoTone
									fontSize='small'
									titleAccess='Edit Location Details'
									className={styles['edit-location-ico']}
								/>
							</ListItemIcon>
						)}
					</ListItemButton>
				)}
				{!locations && !legLocation && (
					<ListItem>
						<ListItemText
							primary='Enter at least 3 characters to begin search'
							className={styles.secondary}
						/>
					</ListItem>
				)}
				{locations && locations.length === 0 ? (
					<ListItem>
						<ListItemText
							primary='No Location Results'
							className={styles.secondary}
						/>
					</ListItem>
				) : (
					locations?.map((location, index) => (
						<ListItemButton
							key={index}
							onClick={() => {
								onChange(mapLocListItemToLegLoc(location));
							}}
							className={styles['list-item']}
						>
							<ListItemText
								primary={`${location.name} (${location.id})`}
								secondary={`${location.addr1} ${location.city}, ${location.state} ${location.zip}`}
							/>
							{!selectionOnly && (
								<ListItemIcon
									title='Edit Location'
									className={styles['list-item-ico']}
									onClick={(event) => {
										event.stopPropagation();
										event.preventDefault();
										closeMenu();
										editLocation(location.id);
									}}
								>
									<EditTwoTone
										fontSize='small'
										titleAccess='Edit Location Details'
										className={styles['edit-location-ico']}
									/>
								</ListItemIcon>
							)}
						</ListItemButton>
					))
				)}
			</List>
		</div>
	);
}
